import GameService from '../services/game.service';

import {
    setTeamsList,
    setSeasonList,
    setLeagueList,
    setPlayersList,
    setGamesList,
    setStandingList,
    setPositions,
    setRoles,
    setAgeGroups,
    setPlayersStatsAdvanceSummaryList,
    setGoalkeepersStatsAdvanceSummaryList,
    setAllPlayersStatsUnified
} from '../helpers/coachSlice';

import { getSeasonList, getLeagueList, getPlayerList } from '../helpers/coach';

export const getLists = () => (dispatch) => {
    const fetchDataPromises = [
        GameService.getAllMyCoachTeam().catch((error) => { console.error('Error in getAllMyCoachTeam:', error); return []; }),
        GameService.getAllPlayersByCoach().catch((error) => { console.error('Error in getAllPlayersByCoach:', error); return []; }),
        GameService.getTeamInitialStanding().catch((error) => { console.error('Error in getTeamInitialStanding:', error); return []; }),
        GameService.getAllGamesByCoach(null, null, null, null).catch((error) => { console.error('Error in getAllGamesByCoach:', error); return []; }),
        GameService.getAllPositions().catch((error) => { console.error('Error in getAllPositions:', error); return []; }),
        GameService.getAllRoles().catch((error) => { console.error('Error in getAllRoles:', error); return []; }),
        GameService.getAllAgeGroups().catch((error) => { console.error('Error in getAllAgeGroups:', error); return []; })
    ];

    Promise.all(fetchDataPromises)
        .then((results) => {
            dispatch(setTeamsList(results[0] || []));
            dispatch(setSeasonList(getSeasonList(results[0] || [])));
            dispatch(setLeagueList(getLeagueList(results[0] || [])));
            dispatch(setPlayersList(results[1] || []));
            dispatch(setStandingList(results[2] || []));
            dispatch(setGamesList(results[3] || []));
            dispatch(setPositions(results[4] || []));
            dispatch(setRoles(results[5] || []));
            dispatch(setAgeGroups(results[6] || []));
        })
        .catch((error) => {
            console.error('An error occurred while fetching data:', error);
        });
};

export const getGames = (seasonID) => async (dispatch) => {
    await GameService.getAllGamesByCoach(seasonID === 'none' ? null : seasonID.id, null, null, null).then((res) => {
        dispatch(setGamesList(res));
    });
};

export const getPlayersStatsAdvanceSummaryList = (seasonFilter, leagueIds, teamIds, gameIds, playerId) => async (dispatch) => {
    // Guard: avoid heavy query that returns ALL players when no filters are provided
    const hasTeamFilter = teamIds && teamIds.length > 0;
    const hasPlayerFilter = playerId !== null && playerId !== undefined;
    if (!hasTeamFilter && !hasPlayerFilter) {
        // Do not fetch without at least a team or player filter to prevent expensive queries
        return;
    }

    dispatch(setPlayersStatsAdvanceSummaryList({ season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name, data: [] }));
    try {
        const res = await GameService.getPlayersStatsAdvanceSummary({
            seasonId: seasonFilter === 'none' ? null : seasonFilter.id,
            leagueId: null,
            gameId: null,
            teamId: teamIds.length > 0 ? teamIds.join(',') : null,
            playerId: playerId,
            gameTime: null,
            courtAreaId: null,
            insidePaint: null,
            homeAway: null,
            gameResult: null
        });
        dispatch(setPlayersStatsAdvanceSummaryList({ season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name, data: res }));
    } catch (error) {
        console.error('Failed to fetch player advance summary stats:', error);
    }
};

// null, null, null, user_id
export const getPlayersList =
    (seasonFilter, leagueIds = [], teamIds = []) =>
    async (dispatch) => {
        const payload = {
            seasonId: seasonFilter && seasonFilter !== 'none' ? seasonFilter.id ?? seasonFilter : null,
            leagueId: leagueIds.length ? leagueIds : null,
            teamId: teamIds.length ? teamIds : null
        };
        const players = await GameService.getPlayersList(payload);
        dispatch(setPlayersList(getPlayerList(players)));
    };

export const getGoalkeepersStatsAdvanceSummaryList = (seasonFilter, leagueIds, teamIds, gameIds, playerId) => async (dispatch) => {
    // Guard: require at least a season filter to avoid fetching all goalkeepers from all seasons
    const hasSeasonFilter = seasonFilter && seasonFilter !== 'none';
    const hasTeamFilter = teamIds && teamIds.length > 0;
    const hasPlayerFilter = playerId !== null && playerId !== undefined;

    // Allow fetch if we have a season filter (even without team filter)
    // This allows coaches to see all their goalkeepers across all their teams for a season
    if (!hasSeasonFilter && !hasTeamFilter && !hasPlayerFilter) {
        console.warn('getGoalkeepersStatsAdvanceSummaryList: No filters provided, skipping fetch');
        return;
    }

    dispatch(setGoalkeepersStatsAdvanceSummaryList({ season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name, data: [] }));
    try {
        const res = await GameService.getGoalkeepersStatsAdvanceSummary({
            seasonId: seasonFilter === 'none' ? null : seasonFilter.id,
            leagueId: null,
            gameId: null,
            teamId: teamIds.length > 0 ? teamIds.join(',') : null,
            playerId: playerId,
            gameTime: null,
            courtAreaId: null,
            insidePaint: null,
            homeAway: null,
            gameResult: null
        });
        dispatch(setGoalkeepersStatsAdvanceSummaryList({ season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name, data: res }));
    } catch (error) {
        console.error('Failed to fetch goalkeeper advance summary stats:', error);
    }
};

// Unified action: fetches ALL players (field players + goalkeepers) in a single call
// Frontend filters by position_id: 16 = Goalkeeper, others = Field players
export const getAllPlayersStatsUnified = (seasonFilter, leagueIds, teamIds, gameIds, playerId) => async (dispatch) => {
    // Guard: require at least a season filter to avoid fetching all players from all seasons
    const hasSeasonFilter = seasonFilter && seasonFilter !== 'none';
    const hasTeamFilter = teamIds && teamIds.length > 0;
    const hasPlayerFilter = playerId !== null && playerId !== undefined;

    if (!hasSeasonFilter && !hasTeamFilter && !hasPlayerFilter) {
        console.warn('getAllPlayersStatsUnified: No filters provided, skipping fetch');
        return;
    }

    dispatch(setAllPlayersStatsUnified({ season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name, data: [] }));
    try {
        const res = await GameService.getAllPlayersStatsUnified({
            seasonId: seasonFilter === 'none' ? null : seasonFilter.id,
            leagueId: null,
            gameId: null,
            teamId: teamIds.length > 0 ? teamIds.join(',') : null,
            playerId: playerId,
            gameTime: null,
            courtAreaId: null,
            insidePaint: null,
            homeAway: null,
            gameResult: null
        });
        dispatch(setAllPlayersStatsUnified({ season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name, data: res }));
    } catch (error) {
        console.error('Failed to fetch unified player stats:', error);
    }
};
